import {
    CheckCircleOutline,
    ErrorOutline,
    InfoOutlined,
    PriorityHighRounded,
    WarningAmberOutlined,
} from 'components/icons';
import { Alert } from 'components/ui/alert';
import { ReactNode } from 'react';
import { cn } from 'utils/cn';

/** `blocked` is the louder error, for a condition that stops the user: a stronger tint inside a red border. */
type Severity = 'info' | 'success' | 'warning' | 'error' | 'blocked';

const SEVERITY: Record<Severity, { className: string; icon: ReactNode }> = {
    info: { className: 'bg-info-lighter', icon: <InfoOutlined className="text-info" /> },
    success: { className: 'bg-success-lighter', icon: <CheckCircleOutline className="text-success" /> },
    warning: { className: 'bg-warning-lighter', icon: <WarningAmberOutlined className="text-warning" /> },
    error: { className: 'bg-error-lighter', icon: <ErrorOutline className="text-error" /> },
    blocked: { className: 'border-error bg-error-light', icon: <PriorityHighRounded className="text-error" /> },
};

interface Props {
    severity: Severity;
    /** Replaces the severity's own glyph. */
    icon?: ReactNode;
    /** Sits at the right edge, after the message. */
    action?: ReactNode;
    /**
     * `status` for a notice that is part of the page as it opens (a record's standing banner): read in its turn
     * instead of interrupting. The default announces at once, for a banner raised by what the user just did.
     */
    role?: 'alert' | 'status';
    /**
     * `items-start` for a message of several lines, so the glyph stays by its first line; `flex-wrap` drops the
     * action under the message on a narrow screen.
     */
    className?: string;
    children: ReactNode;
}

/**
 * A tinted status banner: a glyph in the severity's colour, then a semibold message. Callers that
 * want body-weight text wrap their copy in `font-normal`.
 */
export function DelegationAlert({ severity, icon, action, role = 'alert', className, children }: Props): ReactNode {
    return (
        <Alert
            role={role}
            className={cn(
                'text-fg flex items-center gap-2 rounded-lg border-transparent py-2 pr-4 pl-3 font-semibold',
                SEVERITY[severity].className,
                className
            )}
        >
            {/* A wrapper, not a bare svg child: the kit alert lays a direct svg out as a grid column and nudges
                it down. The wrapper pins any glyph, a caller's included, to 16px. */}
            <span className="flex w-6 shrink-0 justify-center [&>svg]:size-4">{icon ?? SEVERITY[severity].icon}</span>
            <div className="min-w-0 flex-[1_1_16rem]">{children}</div>
            {action ? <div className="ml-auto shrink-0 pl-4">{action}</div> : null}
        </Alert>
    );
}
